/**
 * Last-N citizen advisor requests for this browser (demo continuity).
 * Lightweight index only — restore loads the discovery session from the server.
 */

const STORAGE_KEY = 'if-citizen-request-history-v1';
const MAX = 5;

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readAll() {
  if (typeof window === 'undefined') return [];
  try {
    const data = safeParse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(data?.requests) ? data.requests : [];
  } catch {
    return [];
  }
}

function writeAll(requests) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      requests: (requests || []).slice(0, MAX),
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    /* quota / private mode */
  }
}

export function listCitizenRequestHistory({ packageId, limit = MAX } = {}) {
  const want = packageId ? String(packageId).trim().toLowerCase() : '';
  return readAll()
    .filter((r) => !want || String(r.packageId || '').toLowerCase() === want)
    .slice(0, Math.max(1, Number(limit) || MAX));
}

/**
 * Upsert one history row (keyed by sessionId). Newest first, cap at 5.
 */
export function rememberCitizenRequest(entry = {}) {
  const sessionId = String(entry.sessionId || '').trim();
  if (!sessionId) return listCitizenRequestHistory();
  const now = new Date().toISOString();
  const next = {
    sessionId,
    packageId: entry.packageId || entry.jurisdictionId || null,
    title: String(
      entry.title
      || entry.primaryTrack
      || entry.proposedUse
      || entry.description
      || entry.address
      || 'Advisor request',
    ).slice(0, 120),
    address: entry.address || null,
    proposedUse: entry.proposedUse || entry.description || null,
    primaryTrack: entry.primaryTrack || null,
    updatedAt: now,
    createdAt: entry.createdAt || now,
  };
  const prev = readAll().filter((r) => r.sessionId !== sessionId);
  writeAll([next, ...prev]);
  return listCitizenRequestHistory({ packageId: next.packageId });
}

export function removeCitizenRequest(sessionId) {
  const id = String(sessionId || '').trim();
  if (!id) return listCitizenRequestHistory();
  writeAll(readAll().filter((r) => r.sessionId !== id));
  return listCitizenRequestHistory();
}

export function clearCitizenRequestHistory() {
  writeAll([]);
}

/** Build a history entry from current advise structured output + form. */
export function historyEntryFromAdvise({
  structuredOutput,
  formPrefill,
  packageId,
  sessionId,
} = {}) {
  const out = structuredOutput?.result || structuredOutput || {};
  const routing = out.routing || structuredOutput?.routing || null;
  const form = formPrefill || out.form_prefill || structuredOutput?.form_prefill || {};
  const sid = sessionId
    || routing?.sessionId
    || structuredOutput?.session?.sessionId
    || out.session?.sessionId
    || null;
  if (!sid) return null;
  const track = routing?.primaryTrackLabel || routing?.primaryTrack || null;
  if (!track && !form.address && !form.description) return null;
  return {
    sessionId: sid,
    packageId: packageId || routing?.packageId || structuredOutput?.packageId || null,
    title: track || form.proposedUse || form.description || form.address || 'Advisor request',
    address: form.address || form.location || form.projectAddress || null,
    proposedUse: form.proposedUse || form.description || form.projectDescription || null,
    primaryTrack: track,
  };
}
